import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Move, MousePointerClick, Power } from "lucide-react";
import { isClickSfxOn, setClickSfxOn } from "./ClickSound";

const STORAGE_ON = "sat_ambient_on";
const STORAGE_VOL = "sat_ambient_vol";
const STORAGE_POS = "sat_ambient_pos";
const DEFAULT_VOL = 0.55;

/* --- Generative deep-space lofi ambient pad ---
 * D minor 9 chord over three octaves + slow arpeggio sparkles + subtle vinyl
 * crackle for lofi warmth. Each voice has its own slow amplitude and detune
 * LFOs so no two seconds sound the same. Long feedback-delay for cavernous
 * space reverb. Fully synthesised — no audio files, no copyright, loops
 * forever. */

/* Effective max gain — soft-clip + reduced feedback let this run safely at a
 * clearly audible level without the earlier "rupture" self-oscillation. */
const MAX_GAIN = 0.6;

/* Soft-clip curve (tanh) — smooths any peaks above ±0.8 into a gentle knee
 * instead of hard clipping into digital fuzz. */
function makeSoftClipCurve(samples = 1024) {
  const c = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    c[i] = Math.tanh(x * 1.6) / Math.tanh(1.6);
  }
  return c;
}

function createSpacePad(ctx) {
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.0001;

  // Soft-clip stage prevents the ragged distortion the user was hearing when
  // the delay-feedback + chord sum briefly exceeded 0 dBFS.
  const clip = ctx.createWaveShaper();
  clip.curve = makeSoftClipCurve();
  clip.oversample = "2x";
  master.connect(clip).connect(ctx.destination);

  // Slight high-shelf roll-off for lofi warmth
  const shelf = ctx.createBiquadFilter();
  shelf.type = "highshelf";
  shelf.frequency.value = 2400;
  shelf.gain.value = -5;
  // Modest make-up gain (down from 2.4 → 1.3 to avoid runaway with delay fb)
  const makeup = ctx.createGain();
  makeup.gain.value = 1.3;
  shelf.connect(makeup).connect(master);

  // Global lowpass with slow LFO to sweep timbre over minutes
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 1500;
  lp.Q.value = 0.6;
  const lpLFO = ctx.createOscillator();
  const lpLFOAmt = ctx.createGain();
  lpLFO.frequency.value = 0.035;
  lpLFOAmt.gain.value = 600;
  lpLFO.connect(lpLFOAmt).connect(lp.frequency);
  lpLFO.start(now);
  lp.connect(shelf);

  // Long feedback delay for space reverb — feedback reduced (0.58 → 0.32) so
  // the loop gain stays well under 1.0 and the sound never "ruptures".
  const delay = ctx.createDelay(4);
  delay.delayTime.value = 1.15;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.32;
  const wet = ctx.createGain();
  wet.gain.value = 0.45;
  delay.connect(feedback);
  feedback.connect(delay);
  delay.connect(wet).connect(shelf);
  lp.connect(delay);

  // D minor 9 spread — hopeful minor, spacious.
  const notes = [
    { f: 73.42,  type: "sine",     amp: 0.55 },  // D2 fundament
    { f: 110.00, type: "sine",     amp: 0.42 },  // A2 fifth
    { f: 146.83, type: "sine",     amp: 0.32 },  // D3
    { f: 174.61, type: "triangle", amp: 0.24 },  // F3 minor third
    { f: 220.00, type: "sine",     amp: 0.20 },  // A3
    { f: 293.66, type: "sine",     amp: 0.14 },  // D4
    { f: 523.25, type: "triangle", amp: 0.08 },  // C5 ninth (breath)
  ];

  const nodes = [];
  notes.forEach((n, i) => {
    const o = ctx.createOscillator();
    o.type = n.type;
    o.frequency.value = n.f;

    // Detune LFO — spacey drift
    const detLFO = ctx.createOscillator();
    const detAmt = ctx.createGain();
    detLFO.type = "sine";
    detLFO.frequency.value = 0.020 + Math.random() * 0.040;
    detAmt.gain.value = 6 + Math.random() * 4;
    detLFO.connect(detAmt).connect(o.detune);
    detLFO.start(now);

    // Voice gain with breathing LFO
    const g = ctx.createGain();
    const baseline = ctx.createConstantSource();
    baseline.offset.value = n.amp * 0.60;
    baseline.start(now);
    baseline.connect(g.gain);

    const ampLFO = ctx.createOscillator();
    const ampAmt = ctx.createGain();
    ampLFO.type = "sine";
    ampLFO.frequency.value = 0.045 + Math.random() * 0.09;
    ampAmt.gain.value = n.amp * 0.40;
    ampLFO.connect(ampAmt).connect(g.gain);
    ampLFO.start(now + i * 0.3);

    o.connect(g).connect(lp);
    o.start(now);
    nodes.push({ o, g, detLFO, ampLFO, baseline });
  });

  // Vinyl crackle bed — white-noise burst buffer looped at low volume
  const nBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const nData = nBuf.getChannelData(0);
  for (let i = 0; i < nData.length; i++) {
    // Sparse crackle: mostly silence with occasional pops
    nData[i] = Math.random() < 0.008 ? (Math.random() * 2 - 1) * 0.6 : 0;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = nBuf;
  noise.loop = true;
  const noiseHP = ctx.createBiquadFilter();
  noiseHP.type = "highpass";
  noiseHP.frequency.value = 1500;
  const noiseGain = ctx.createGain();
  noiseGain.gain.value = 0.08;
  noise.connect(noiseHP).connect(noiseGain).connect(shelf);
  noise.start(now);

  // Sparkle bell — plays a random note from the D-min scale every 8-14s
  const scale = [293.66, 349.23, 440.00, 523.25, 587.33, 698.46, 880.00]; // D-min above
  let stopped = false;
  const scheduleSparkle = () => {
    if (stopped) return;
    const wait = 8 + Math.random() * 6;
    setTimeout(() => {
      if (stopped || ctx.state === "closed") return;
      const t = ctx.currentTime;
      const f = scale[Math.floor(Math.random() * scale.length)];
      const bell = ctx.createOscillator();
      const bg = ctx.createGain();
      bell.type = "sine";
      bell.frequency.value = f;
      bg.gain.setValueAtTime(0.0001, t);
      bg.gain.exponentialRampToValueAtTime(0.06, t + 0.15);
      bg.gain.exponentialRampToValueAtTime(0.0001, t + 3.5);
      bell.connect(bg).connect(delay); // dry into the delay for maximum shimmer
      bell.start(t);
      bell.stop(t + 3.6);
      scheduleSparkle();
    }, wait * 1000);
  };
  scheduleSparkle();

  return {
    ctx,
    setVolume: (v) => {
      // User slider is 0..1; the effective gain is capped to MAX_GAIN so the
      // chord+delay tail never spills into distortion. setTargetAtTime gives a
      // reliable exponential glide (τ = 1.2s) — pad swells in over ~5-6s.
      const target = Math.max(0.0001, Math.min(1, v)) * MAX_GAIN;
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(Math.max(0.0001, master.gain.value || 0.0001), t);
      master.gain.setTargetAtTime(target, t, 1.2);
    },
    stop: () => {
      stopped = true;
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(Math.max(0.0001, master.gain.value || 0.0001), t);
      // Long tail-out: τ = 1.0s → effectively silent by ~4.5s.
      master.gain.setTargetAtTime(0.0001, t, 1.0);
      setTimeout(() => {
        nodes.forEach(({ o, detLFO, ampLFO, baseline }) => {
          try { o.stop(); detLFO.stop(); ampLFO.stop(); baseline.stop(); } catch { /* ignore */ }
        });
        try { lpLFO.stop(); } catch { /* ignore */ }
        try { noise.stop(); } catch { /* ignore */ }
        try { ctx.close(); } catch { /* ignore */ }
      }, 5200);
    },
  };
}

/** Ambient deep-space pad toggle + volume slider + freely draggable.
 *  Audio is generated procedurally via Web Audio — no external files, no
 *  copyright, so the loop is genuinely license-free. */
export default function AmbientSound() {
  const padRef = useRef(null);
  const panelRef = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0, dragging: false });
  const [on, setOn] = useState(() => localStorage.getItem(STORAGE_ON) === "1");
  const [sfxOn, setSfxOn] = useState(() => isClickSfxOn());
  const [vol, setVol] = useState(() => {
    const raw = parseFloat(localStorage.getItem(STORAGE_VOL));
    return Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : DEFAULT_VOL;
  });
  const [pos, setPos] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_POS) || "null");
      if (raw && typeof raw.x === "number" && typeof raw.y === "number") return raw;
    } catch { /* ignore */ }
    return null;
  });

  const startPad = () => {
    if (padRef.current) return padRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    const pad = createSpacePad(ctx);
    padRef.current = pad;
    pad.setVolume(vol);
    return pad;
  };

  const stopPad = () => {
    if (!padRef.current) return;
    padRef.current.stop();
    padRef.current = null;
  };

  const toggle = async () => {
    const next = !on;
    setOn(next);
    localStorage.setItem(STORAGE_ON, next ? "1" : "0");
    if (next) {
      const pad = startPad();
      if (pad?.ctx?.state === "suspended") {
        try { await pad.ctx.resume(); } catch { /* ignore */ }
      }
      // Re-schedule the fade after resume so the ramp sits on the running clock.
      if (pad) pad.setVolume(vol);
    } else {
      stopPad();
    }
    window.dispatchEvent(new CustomEvent("sat:pad-state", { detail: { on: next, vol } }));
  };

  const onVolChange = (e) => {
    const v = parseFloat(e.target.value);
    setVol(v);
    localStorage.setItem(STORAGE_VOL, String(v));
    if (padRef.current && on) padRef.current.setVolume(v);
    window.dispatchEvent(new CustomEvent("sat:pad-state", { detail: { on, vol: v } }));
  };

  // Bridge events from the persistent bottom-of-screen bar
  useEffect(() => {
    const onExternalToggle = async (e) => {
      const next = !!e.detail?.on;
      if (next === on) return;
      setOn(next);
      localStorage.setItem(STORAGE_ON, next ? "1" : "0");
      if (next) {
        const pad = startPad();
        if (pad?.ctx?.state === "suspended") {
          try { await pad.ctx.resume(); } catch { /* ignore */ }
        }
        if (pad) pad.setVolume(vol);
      } else {
        stopPad();
      }
    };
    const onExternalVol = (e) => {
      const v = parseFloat(e.detail?.vol);
      if (!Number.isFinite(v)) return;
      setVol(v);
      localStorage.setItem(STORAGE_VOL, String(v));
      if (padRef.current && on) padRef.current.setVolume(v);
    };
    window.addEventListener("sat:pad-toggle", onExternalToggle);
    window.addEventListener("sat:pad-vol", onExternalVol);
    return () => {
      window.removeEventListener("sat:pad-toggle", onExternalToggle);
      window.removeEventListener("sat:pad-vol", onExternalVol);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  useEffect(() => {
    const onVisibility = () => {
      const pad = padRef.current;
      if (!pad || !on) return;
      if (document.hidden) pad.setVolume(0.0001);
      else pad.setVolume(vol);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [on, vol]);

  useEffect(() => () => { stopPad(); }, []);

  // Keep sfxOn state in sync if it's toggled from anywhere else.
  useEffect(() => {
    const onChange = () => setSfxOn(isClickSfxOn());
    window.addEventListener("sat:sfx-changed", onChange);
    return () => window.removeEventListener("sat:sfx-changed", onChange);
  }, []);

  const toggleSfx = () => {
    const next = !sfxOn;
    setSfxOn(next);
    setClickSfxOn(next);
  };

  // Drag handlers — panel can be repositioned anywhere on screen. Position is
  // persisted to localStorage. Supports both mouse and touch.
  const onDragStart = (clientX, clientY) => {
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      origX: rect.left,
      origY: rect.top,
      dragging: true,
    };
  };
  const onDragMove = (clientX, clientY) => {
    const st = dragRef.current;
    if (!st.dragging) return;
    const el = panelRef.current;
    const w = el?.offsetWidth || 260;
    const h = el?.offsetHeight || 44;
    let x = st.origX + (clientX - st.startX);
    let y = st.origY + (clientY - st.startY);
    x = Math.max(8, Math.min(window.innerWidth - w - 8, x));
    y = Math.max(8, Math.min(window.innerHeight - h - 8, y));
    setPos({ x, y });
  };
  const onDragEnd = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    if (pos) localStorage.setItem(STORAGE_POS, JSON.stringify(pos));
  };

  useEffect(() => {
    const mMove = (e) => onDragMove(e.clientX, e.clientY);
    const tMove = (e) => {
      const t = e.touches?.[0]; if (!t) return;
      onDragMove(t.clientX, t.clientY);
    };
    window.addEventListener("mousemove", mMove);
    window.addEventListener("mouseup", onDragEnd);
    window.addEventListener("touchmove", tMove, { passive: false });
    window.addEventListener("touchend", onDragEnd);
    return () => {
      window.removeEventListener("mousemove", mMove);
      window.removeEventListener("mouseup", onDragEnd);
      window.removeEventListener("touchmove", tMove);
      window.removeEventListener("touchend", onDragEnd);
    };
  }, [pos]);

  const startMouse = (e) => { e.preventDefault(); onDragStart(e.clientX, e.clientY); };
  const startTouch = (e) => {
    const t = e.touches?.[0]; if (!t) return;
    onDragStart(t.clientX, t.clientY);
  };

  const volPct = Math.round(vol * 100);
  const posStyle = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
    : {};

  return (
    <div
      ref={panelRef}
      className={`fixed z-50 sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-2 pl-2 flex items-center gap-3 font-display text-[11px] tracking-[0.2em] uppercase ${
        pos ? "" : "bottom-20 left-6"
      }`}
      style={posStyle}
      data-testid="ambient-sound-panel"
    >
      <button
        onMouseDown={startMouse}
        onTouchStart={startTouch}
        data-testid="ambient-sound-drag"
        aria-label="Drag ambient sound panel"
        title="Drag to reposition"
        className="cursor-grab active:cursor-grabbing text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)] px-1 select-none touch-none"
      >
        <Move size={12} />
      </button>

      <button
        onClick={toggle}
        data-testid="ambient-sound-toggle"
        aria-pressed={on}
        aria-label={on ? "Mute deep-space pad" : "Play deep-space pad"}
        className={`flex items-center gap-2 transition-colors ${
          on
            ? "text-[color:var(--sat-cyan)]"
            : "text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)]"
        }`}
      >
        {on ? <Volume2 size={13} /> : <VolumeX size={13} />}
        <span>DEEP SPACE</span>
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full ${on ? "bg-[color:var(--sat-cyan)] animate-pulse" : "bg-[color:var(--sat-text-dim)]/50"}`}
          aria-hidden
        />
      </button>

      <div className="flex items-center gap-2 pl-3 border-l border-[color:var(--sat-border-bright)]">
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={vol}
          onChange={onVolChange}
          aria-label="Ambient audio volume"
          data-testid="ambient-sound-volume"
          className="sat-vol-slider"
          style={{ ["--fill"]: `${volPct}%` }}
          disabled={!on}
        />
        <span
          className={`w-8 text-right tabular-nums ${on ? "text-[color:var(--sat-cyan)]" : "text-[color:var(--sat-text-dim)]"}`}
          data-testid="ambient-sound-volume-value"
        >
          {volPct}
        </span>
      </div>

      <button
        onClick={toggleSfx}
        data-sfx-silent
        data-testid="click-sfx-toggle"
        aria-pressed={sfxOn}
        aria-label={sfxOn ? "Disable click sound" : "Enable click sound"}
        title={sfxOn ? "Click SFX: ON" : "Click SFX: OFF"}
        className={`flex items-center gap-1.5 pl-3 border-l border-[color:var(--sat-border-bright)] transition-colors ${
          sfxOn ? "text-[color:var(--sat-cyan)]" : "text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)]"
        }`}
      >
        <MousePointerClick size={13} />
        <span className="text-[10px] tracking-[0.2em]">{sfxOn ? "SFX" : "SFX·OFF"}</span>
      </button>

      <button
        onClick={() => window.dispatchEvent(new Event("sat:boot-replay"))}
        data-testid="boot-replay"
        aria-label="Replay boot sequence"
        title="Replay ASTRONATLAS boot sequence"
        className="flex items-center gap-1.5 pl-3 border-l border-[color:var(--sat-border-bright)] text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)] transition-colors"
      >
        <Power size={13} />
        <span className="text-[10px] tracking-[0.2em]">BOOT</span>
      </button>

      <div
        data-testid="ambient-sound-credit"
        className="hidden md:block pl-3 border-l border-[color:var(--sat-border-bright)] text-[9px] leading-tight text-[color:var(--sat-text-dim)]"
        title="Deep-space pad generated procedurally in your browser — no copyright"
      >
        <div>PAD // GENERATIVE</div>
        <div className="opacity-70">D-MIN9 · SPARKLE · VINYL WARMTH</div>
      </div>
    </div>
  );
}
